import { useId } from 'react';
import type { ThermalInputs } from '../../engines/types';
import type { RoofConfig, SaunaDimensions } from '../../engines/types';
import type { SaunaType } from '../../types/design';
import { useT } from '../../i18n';
import { useBuilderToast } from './BuilderToast';
import { calculateHeaterPlacement } from '../../domain/chimney/calculateHeaterPlacement';
import type { BuilderRunningCostSummary } from '../../pages/builder/runningCostSummary';
import {
    type HeaterModelFit,
    heaterModelsFor,
    resolveHeaterModel,
} from '../../domain/heater/heaterCatalog';
import { formatHeaterModelFit } from './formatHeaterModelFit';

interface ThermalInputsEditorProps {
    saunaType: SaunaType;
    dimensions: SaunaDimensions;
    roofConfig: RoofConfig;
    partitionSplit: number;
    thermalInputs: ThermalInputs;
    /** Live consequence of the heater choice; absent when not derivable. */
    runningCost?: BuilderRunningCostSummary | null;
    /** Whether the chosen catalog model suits the room (issue 159). */
    heaterModelFit?: HeaterModelFit;
    onThermalInputsChange: (thermal: ThermalInputs) => void;
}

export function ThermalInputsEditor({
    saunaType,
    dimensions,
    roofConfig,
    partitionSplit,
    thermalInputs,
    runningCost,
    heaterModelFit,
    onThermalInputsChange,
}: ThermalInputsEditorProps) {
    const t = useT();
    const toast = useBuilderToast();
    const consequenceHeadingId = useId();
    const updateThermal = <K extends keyof ThermalInputs>(key: K, value: ThermalInputs[K]) => {
        onThermalInputsChange({ ...thermalInputs, [key]: value });
    };

    const handleHeaterChange = (value: ThermalInputs['heaterType']) => {
        // A model belongs to one type: switching ved ↔ el drops it.
        onThermalInputsChange({ ...thermalInputs, heaterType: value, heaterModelId: null });
        const label = value === 'wood' ? t.thermal_heaterWood : t.thermal_heaterElectric;
        toast.show(t.toast_heaterChanged.replace('{type}', label), 'info');
    };

    const selectedModel = resolveHeaterModel(thermalInputs);
    const modelFitText = formatHeaterModelFit(t, heaterModelFit);

    const placement = calculateHeaterPlacement({
        saunaType,
        widthM: dimensions.width,
        lengthM: dimensions.length,
        heaterType: thermalInputs.heaterType,
        heaterModelId: thermalInputs.heaterModelId,
        roofKind: roofConfig.kind,
        partitionSplit,
        preferredCenterZMm: thermalInputs.heaterPlacementZMm,
    });

    return (
        <div className="inspector-section-body">
            <div className="thermal-editor">
                <div className="input-row">
                    <label htmlFor="heater-type">{t.thermal_heaterLabel}</label>
                    <select
                        id="heater-type"
                        value={thermalInputs.heaterType}
                        onChange={(e) => handleHeaterChange(e.target.value as ThermalInputs['heaterType'])}
                    >
                        <option value="wood">{t.thermal_heaterWood}</option>
                        <option value="electric">{t.thermal_heaterElectric}</option>
                    </select>
                </div>
                <div className="input-row">
                    <label htmlFor="heater-model">{t.thermal_heaterModelLabel}</label>
                    <select
                        id="heater-model"
                        value={selectedModel?.id ?? ''}
                        onChange={(e) => updateThermal('heaterModelId', e.target.value || null)}
                    >
                        <option value="">{t.thermal_heaterModelNone}</option>
                        {heaterModelsFor(thermalInputs.heaterType).map((model) => (
                            <option key={model.id} value={model.id}>
                                {model.firing === 'outside'
                                    ? `${model.name} (${t.thermal_heaterModelOutside})`
                                    : model.name}
                            </option>
                        ))}
                    </select>
                </div>
                {selectedModel && (
                    <p className="thermal-model-spec">
                        {t.thermal_heaterModelSpec
                            .replace('{kw}', String(selectedModel.powerKw))
                            .replace('{min}', String(selectedModel.roomVolumeM3.min))
                            .replace('{max}', String(selectedModel.roomVolumeM3.max))
                            .replace('{w}', String(selectedModel.bodyMm.width))
                            .replace('{d}', String(selectedModel.bodyMm.depth))
                            .replace('{h}', String(selectedModel.bodyMm.height))}
                        {' · '}
                        <a href={selectedModel.source.url} target="_blank" rel="noopener noreferrer">
                            {t.thermal_heaterModelSource}
                        </a>
                    </p>
                )}
                {selectedModel?.firing === 'outside' && (
                    <p className="thermal-placement-note">{t.thermal_heaterModelOutsideNote}</p>
                )}
                {modelFitText && (
                    <p className="thermal-model-fit" role="status">
                        {modelFitText}
                    </p>
                )}
                {runningCost && (
                    <section className="thermal-consequence" aria-labelledby={consequenceHeadingId}>
                        <p className="thermal-consequence__heading" id={consequenceHeadingId}>
                            {t.runningCost_thermalHeading}
                        </p>
                        <dl className="thermal-consequence__stats">
                            {/* recommendedKw sizes an electric element; the wood
                                model never reads it, so the row is omitted. */}
                            {runningCost.heaterKwLabel && (
                                <div className="thermal-consequence__stat">
                                    <dt>{t.design_heaterLabel}</dt>
                                    <dd>{runningCost.heaterKwLabel}</dd>
                                </div>
                            )}
                            <div className="thermal-consequence__stat">
                                <dt>{t.runningCost_glassLabel}</dt>
                                <dd>{runningCost.glassAreaLabel}</dd>
                            </div>
                            <div className="thermal-consequence__stat">
                                <dt>{t.runningCost_heatUpLabel}</dt>
                                <dd>{runningCost.heatUpLabel}</dd>
                            </div>
                            <div className="thermal-consequence__stat">
                                <dt>{t.runningCost_sessionCostLabel}</dt>
                                <dd>{runningCost.sessionCostLabel}</dd>
                            </div>
                        </dl>
                        <p className="thermal-consequence__note">{runningCost.costNote}</p>
                    </section>
                )}
                <div className="input-row">
                    <label htmlFor="stone-area">{t.thermal_stoneAreaLabel}</label>
                    <input
                        id="stone-area"
                        type="number"
                        min={0}
                        step={0.1}
                        value={thermalInputs.stoneAreaSqm}
                        onChange={(e) => updateThermal('stoneAreaSqm', Number(e.target.value))}
                    />
                </div>
                <label className="thermal-checkbox">
                    <input
                        type="checkbox"
                        checked={thermalInputs.isOutdoorLogCabin}
                        onChange={(e) => updateThermal('isOutdoorLogCabin', e.target.checked)}
                    />
                    {t.thermal_logCabin}
                </label>
                {saunaType !== 'tvarum' && <label className="thermal-checkbox">
                    <input
                        type="checkbox"
                        checked={thermalInputs.includesShower}
                        onChange={(e) => updateThermal('includesShower', e.target.checked)}
                    />
                    {t.thermal_shower}
                </label>}
                {placement.mode === 'slot-select' && (
                    <div className="input-row">
                        <label htmlFor="heater-slot">{t.thermal_heaterPlacementLabel}</label>
                        <select
                            id="heater-slot"
                            value={placement.selectedSlotId}
                            onChange={(event) => {
                                const slot = placement.validSlots.find((candidate) => candidate.id === event.target.value);
                                if (!slot) return;
                                updateThermal('heaterPlacementZMm', slot.centerZMm);
                            }}
                        >
                            {placement.validSlots.map((slot) => (
                                <option key={slot.id} value={slot.id}>
                                    {`${slot.label} • ${slot.clearWidthMm} mm`}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
                {placement.mode === 'fixed' && thermalInputs.heaterType === 'wood' && (
                    <p className="thermal-placement-note">{t.thermal_heaterPlacementFixed}</p>
                )}
            </div>
        </div>
    );
}
